<template>
  <div class="d-flex flex-column gap-6">
    <required-questions :requiredQuestions="requiredQuestions" :requiredParameters="requiredParameters" />

    <sector-radio v-if="indicatedTreatmentsContainsBiologic" v-model="chosenSector" />

    <expandable-box-node title="Relatório" :disabled="!chosenSector && indicatedTreatmentsContainsBiologic">
      <div class="d-flex flex-column gap-4">
        <div class="d-flex flex-row align-center gap-4">
          <select-printing-template v-model="selectedTemplate" type="report" style="max-width: 400px" />
          <v-btn class="new-btn" color="primary" depressed :disabled="isEmpty(reportText)" :loading="printing">
            Imprimir
          </v-btn>
          <v-btn class="new-btn" color="primary" outlined :disabled="!reportAvailable" @click="handleGenerateReport">
            Gerar novamente
          </v-btn>
        </div>
        <long-text-input-component
          label="Texto do relatório"
          v-model="reportText"
          :rows="generatedReport ? 20 : 5"
          :hint="
            manualReport
              ? null
              : 'A partir de agora o relatório está no modo manual e atualizações automáticas futuras estão desabilitadas'
          "
          resizable
          @keyup="handleReportKeypress"
        />
      </div>
    </expandable-box-node>

    <expandable-box-node
      v-if="!chosenSector || chosenSector == 'public' || (chosenSector == 'none' && indicatedTreatmentsContainsLme)"
      title="LME"
      :disabled="!chosenSector && indicatedTreatmentsContainsBiologic"
    >
      <lme-node :lmeAvailable="reportAvailable" />
    </expandable-box-node>
  </div>
</template>

<script>
import { Computed, ObjectLiteral } from '@/_typedefs'
import DownloadFrame from '@/components/DownloadFrame.vue'
import ExpandableBoxNode from '@/components/NewAttendanceForm/FormNodes/ExpandableBox/ExpandableBoxNode.vue'
import SelectComponent from '@/components/NewAttendanceForm/Questions/Basic/Select/Select.component.vue'
import LongTextInputComponent from '@/components/NewAttendanceForm/Questions/Basic/TextInput/LongTextInput.component.vue'
import { isEmpty } from 'lodash'
import { createNamespacedHelpers } from 'vuex'
import LmeNode from './Lme/LmeNode.vue'
import { generateReport } from './reportGenerator'
import RequiredQuestions from './RequiredQuestions/RequiredQuestions.vue'
import SectorRadio from './SectorRadio.vue'
import SelectPrintingTemplate from '@/components/PrintingTemplate/SelectPrintingTemplate.vue'
import { PrintingTemplate } from '@/plugins/api/endpoints/printingTemplate'

const {
  mapMutations: newAttendanceFormMutations,
  mapGetters: newAttendanceFormGetters,
  mapActions: newAttendanceFormActions,
} = createNamespacedHelpers('newAttendanceForm')

export default {
  components: {
    RequiredQuestions,
    LongTextInputComponent,
    ExpandableBoxNode,
    LmeNode,
    SectorRadio,
    SelectPrintingTemplate,
  },
  props: {
    requiredQuestions: {
      type: Object,
      required: true,
    },
    requiredParameters: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      reportText: '',
      manualReport: false,
      generatedReport: false,
      reportAvailability: { questions: false, parameters: false },
      answers: {},
      parameters: {},
      unwatch: null,
      printingTemplates: [],
      /** @type {PrintingTemplate?} */
      selectedTemplate: null,
      printing: false,
      /** @type {'private' | 'public' | 'none' | null} */
      chosenSector: null,
      indicatedTreatmentsContainsBiologic: false,
      indicatedTreatmentsContainsLme: false,
    }
  },
  computed: {
    .../** @type {{attendance: Computed<ObjectLiteral>, treatments: Computed<ObjectLiteral[]>}} */ (
      newAttendanceFormGetters(['attendance', 'treatments'])
    ),
    /** @type {Computed<string[]>} */
    requiredQuestionKeys() {
      return Object.keys(this.requiredQuestions)
    },
    /** @type {Computed<string[]>} */
    requiredParametersKeys() {
      return Object.keys(this.requiredParameters)
    },
    /** @type {Computed<boolean>} */
    reportAvailable() {
      return Object.values(this.reportAvailability).every((it) => !!it)
    },
    /** @type {Computed<string>} */
    printReportUrl() {
      return (
        `${this.$util.apiURL()}/attendance/report/print/${this.attendance.id}` +
        (this.selectedTemplate ? `/${this.selectedTemplate}` : '')
      )
    },
    currentPhase() {
      return this.$route.query.phase
    },
  },
  methods: {
    handleReportKeypress(e) {
      if (e.altKey || e.ctrlKey || e.metaKey) {
        return false
      }
      this.manualReport = /^[a-zA-Z0-9_-]$|^\s$|^Backspace$|^Enter$/.test(e.key)
    },
    ...newAttendanceFormMutations(['updateExtraAnswer']),
    ...newAttendanceFormActions(['saveAllAnswers']),
    async printPdf() {
      await this.saveAllAnswers()
      try {
        this.printing = true
        const blob = await this.$api.printingTemplate.print({
          templateId: this.selectedTemplate.id,
          patientId: this.attendance.patient.id,
          text: this.reportText,
        })
        this.$util.downloadBlob(blob, 'relatório.pdf')
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.printing = false
      }
    },
    handleGenerateReport() {
      this.reportText = generateReport()
    },
    calculateIndicatedTreatmentsData(indicatedTreatments) {
      const indicatedTreatmentsId = Object.keys(indicatedTreatments ?? {})

      const filteredTreatments = this.treatments.filter((treatment) =>
        indicatedTreatmentsId.includes(String(treatment.id)),
      )

      this.indicatedTreatmentsContainsBiologic = filteredTreatments.some(
        (treatment) => treatment.medicine.category.name == 'Biológico',
      )

      this.indicatedTreatmentsContainsLme = filteredTreatments.some((treatment) =>
        treatment.dosages.some((dosage) => dosage.treatmentDosageLme.showLmeInAttendance),
      )
    },
    isEmpty,
  },
  watch: {
    reportText(value) {
      if (isEmpty(value)) {
        return
      }

      this.updateExtraAnswer({ name: 'report', value })
    },
    currentPhase(val) {
      if (val === 'Relatório' && this.reportAvailable && !this.manualReport) {
        this.handleGenerateReport()
      }
    },
    answers: {
      handler(value) {
        this.reportAvailability.questions = this.requiredQuestionKeys
          .map((key) => value[key])
          .every((answer) => !isEmpty(answer))
      },
      deep: true,
    },
    parameters: {
      handler(value) {
        this.reportAvailability.parameters = this.requiredParametersKeys
          .map((key) => value[key])
          .some((parameter) => !isEmpty(parameter))
      },
      deep: true,
    },
    reportAvailable: {
      handler(value) {
        if (value && !this.generatedReport) {
          this.handleGenerateReport()
          this.generatedReport = true
        }
      },
      immediate: true,
    },
  },
  async created() {
    const { items: templates } = await this.$api.getTemplates()
    this.printingTemplates = templates.map(({ id, name }) => ({ id, name }))

    this.reportText = this.$store.getters['newAttendanceForm/extraAnswers']?.report ?? ''
    if (!isEmpty(this.reportText)) {
      this.generatedReport = true
    }

    this.answers = this.$store.getters['newAttendanceForm/answers']
    this.parameters = this.$store.getters['newAttendanceForm/parameters']

    this.calculateIndicatedTreatmentsData(
      this.$store.getters['newAttendanceForm/extraAnswers']?.['indicated-treatments'] ?? {},
    )

    this.unwatch = this.$store.subscribe((mutation, state) => {
      const listeners = {
        'newAttendanceForm/updateAnswer': () => {
          this.answers = Object.assign({}, this.answers, state.newAttendanceForm.answers)
        },
        'newAttendanceForm/updateParameter': () => {
          this.parameters = Object.assign({}, this.parameters, state.newAttendanceForm.parameters)
        },
        'newAttendanceForm/updateExtraAnswer': () => {
          if (mutation.payload.name !== 'indicated-treatments') {
            return
          }
          this.calculateIndicatedTreatmentsData(state.newAttendanceForm.extraAnswers['indicated-treatments'] ?? {})
        },
      }

      listeners[mutation.type]?.()
    })
  },
  beforeDestroy() {
    this.unwatch?.()
  },
}
</script>

<style></style>
